import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Typography from '@mui/material/Typography';
import { Theme, Box } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import { Discount, Shopper, ProductApi } from 'common/types';
import IconButton from '@mui/material/IconButton';
import DiscountDialog from 'components/DiscountDialog';
import { getRandomUserId } from 'services/utils';
import ShopperCardShoppingCart from './ShopperCardShoppingCart';
import moment from 'moment-timezone';
import useShopFormat from 'hooks/useShopFormat';
import { useTranslation } from 'services/localization/useTranslation';
import { RiPercentLine } from 'react-icons/ri';
import { PriceBreakdown } from 'common/components/molecules/PriceBreakdown';
import { getExtendedOrderPricing } from 'common/modules/atoms/pricing';
import * as NewRentalSelectors from 'selectors/NewRentalSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import * as NewRentalActions from 'actions/NewRentalActions';

import {
	dateIsClosed,
	dateIsOutsideOpeningHours,
	getNewEndTime,
} from 'common/utils/dateCalculations';
import { usePermissions } from 'hooks/usePermissions';
import OverbookedBanner from './OverbookedBanner';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import ManualPaymentMethodSelect from './ManualPaymentMethodSelect';
import { isManualShopPaymentMethodObject } from 'common/modules/payments/paymentMethods';
import { ShopManualOnlinePaymentMethodObject } from 'common/modules/payments/types';

interface Props {
	activeShopperId: string;
	changeActiveShopperId: (id: string) => void;
	stockProducts: ProductApi[];
}

const ShoppingCart = (props: Props) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
	const { activeShopperId, changeActiveShopperId } = props;
	const rentalInfo = useSelector(NewRentalSelectors.rentalInfo);
	const { pricing } = rentalInfo;
	const startDate = useSelector(ViewSelectors.newRentalStartDate);
	const endDate = useSelector(ViewSelectors.newRentalEndDate);
	const manualDiscount = pricing.manualDiscount ?? { amount: 0 };
	const sameDay = moment(startDate).isSame(moment(endDate), 'day');
	const { localFormat } = useShopFormat();
	const overbookedVariantIds = useSelector(NewRentalSelectors.overbookedVariantIds);
	const shoppers = useSelector(NewRentalSelectors.shoppers);
	const rentalProducts = useSelector(NewRentalSelectors.products);
	const openingHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException);
	const shopTimeZone = useSelector(ShopSelectors.getShopTimeZone);
	const duration = useSelector(ViewSelectors.newRentalDuration);
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);
	const activeManualPaymentMethodOptions = useSelector(
		ShopSelectors.getActiveManualPaymentMethodOptions,
	);
	const selectedPaymentMethod = useSelector(NewRentalSelectors.paymentMethod);

	const currency = getCurrencyObjectFromCurrencyCode(rentalInfo.pricing.currency, shopCurrency);

	const lang = useSelector(LanguageSelectors.language);

	const { t } = useTranslation();
	const { hasPermission } = usePermissions();
	const allowOverbookings = hasPermission('allow_overbookings');
	const hasOverbookedVariants = !!overbookedVariantIds.length;
	const from = moment(startDate);
	const to = moment(getNewEndTime(from.toISOString(), duration, openingHours, shopTimeZone));
	const isClosed = dateIsClosed(from, openingHours) || dateIsClosed(to, openingHours);
	const startTimeIsOutsideOpeningHours = dateIsOutsideOpeningHours(from, openingHours);
	const endTimeIsOutsideOpeningHours = dateIsOutsideOpeningHours(to, openingHours);
	const isOutsideOpeningHours =
		startTimeIsOutsideOpeningHours || endTimeIsOutsideOpeningHours || isClosed;
	const showBanner = hasOverbookedVariants || isOutsideOpeningHours;

	const addNewShopper = () => {
		const shopperId = getRandomUserId();
		dispatch(NewRentalActions.addNewShopper(shopperId));
		changeActiveShopperId(shopperId);
	};

	const deleteShopper = (e: React.MouseEvent<HTMLElement>, id: string) => {
		if (id === '') {
			return;
		}
		if (shoppers.length === 1) {
			dispatch(NewRentalActions.updateProductsForShopper([], id));
			return;
		}
		if (id === activeShopperId) {
			const newActiveShopperId = shoppers.map((s) => s.id).filter((sId) => sId !== id)[0];
			changeActiveShopperId(newActiveShopperId);
		}
		dispatch(NewRentalActions.deleteShopper(id));
		e.stopPropagation();
	};

	const openDiscountDialog = () => setDiscountDialogOpen(true);

	const handleDiscountDialogClose = (discount: Discount) => {
		setDiscountDialogOpen(false);
		dispatch(NewRentalActions.addRentalDiscount(discount));
	};

	const renderShopperCards = (shoppers: Shopper[]) =>
		shoppers.map((shopper, index) => (
			<ShopperCardShoppingCart
				currency={currency}
				key={shopper.id}
				shopper={shopper}
				rentalProducts={rentalProducts}
				activeShopperId={activeShopperId}
				index={index}
				lang={lang}
				changeActiveShopperId={(shopperId: string) => changeActiveShopperId(shopperId)}
				deleteShopper={(e: React.MouseEvent<HTMLElement>, shopperId: string) =>
					deleteShopper(e, shopperId)
				}
			/>
		));

	const renderDiscountDialog = (manualDiscount: Discount) => (
		<DiscountDialog
			open={discountDialogOpen}
			onClose={handleDiscountDialogClose}
			discount={manualDiscount}
			originalPrice={getExtendedOrderPricing(rentalInfo.pricing).subtotalWithoutManualDiscount}
			currency={currency}
		/>
	);

	const hasMultipleManualMethods = (methods: ShopManualOnlinePaymentMethodObject[]) =>
		methods.length > 1;

	return (
		<React.Fragment>
			<div className={classes.cartView}>
				<Box className={classes.rentalTime}>
					<Typography variant="subtitle2">
						{t('shoppingCart.rentalTime2', 'Order duration:')}
					</Typography>
					{sameDay ? (
						<Typography>
							{localFormat(startDate, 'HH:mm')} - {localFormat(endDate, 'HH:mm')}
						</Typography>
					) : (
						<Box flexDirection="row">
							<Typography>{localFormat(startDate, 'D.M., HH:mm') + ' - '}</Typography>
							<Typography>{localFormat(endDate, 'D.M., HH:mm')}</Typography>
						</Box>
					)}
				</Box>

				{showBanner && (
					<OverbookedBanner
						allowOverbookings={allowOverbookings}
						rentalProducts={rentalProducts}
						overbookedVariantIds={overbookedVariantIds}
						openingHours={{
							isBeforeOpeningHours: startTimeIsOutsideOpeningHours,
							isOutsideOpeningHours: isOutsideOpeningHours,
							isClosed,
						}}
						styling={{ link: true }}
					/>
				)}
				<div className={classes.cartContent}>
					{renderShopperCards(shoppers)}
					<Box p={1}>
						<Button
							variant="outlined"
							color="primary"
							fullWidth
							onClick={addNewShopper}
							className={classes.noFlex}
						>
							{t('shoppingCart.addPersonBtn', '+ Add person')}
						</Button>
					</Box>
				</div>
				<Box padding={0.25}>
					<IconButton
						onClick={() => openDiscountDialog()}
						aria-label="Discount"
						size="large"
						disabled={!hasPermission('edit_booking')}
					>
						<RiPercentLine />
					</IconButton>
				</Box>
				{hasMultipleManualMethods(activeManualPaymentMethodOptions) &&
					!!selectedPaymentMethod &&
					isManualShopPaymentMethodObject(selectedPaymentMethod) && (
						<Box className={classes.paymentMethodSection}>
							<ManualPaymentMethodSelect
								options={activeManualPaymentMethodOptions}
								value={selectedPaymentMethod}
								onChange={(method) => dispatch(NewRentalActions.updatePaymentMethod(method))}
								sx={{
									padding: (theme) => theme.spacing(2, 2, 1, 2),
								}}
							/>
						</Box>
					)}

				<Box className={classes.pricingSection}>
					<PriceBreakdown pricing={pricing} shopCurrency={currency} t={t} lang={lang} />
				</Box>
			</div>
			{discountDialogOpen && renderDiscountDialog(manualDiscount)}
		</React.Fragment>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		cartView: {
			display: 'flex',
			flexDirection: 'column',
			flex: 1,
		},
		cartContent: {
			display: 'block',
			paddingBottom: 8,
			overflow: 'auto',
			flex: 1,
		},
		noFlex: {
			flex: '0 0 auto',
		},
		rentalTime: {
			padding: theme.spacing(1),
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			borderBottom: '1px solid rgba(0,0,0,0.05)',
		},
		pricingSection: {
			padding: theme.spacing(2),
			borderTop: '1px solid rgba(0,0,0,0.05)',
		},
		paymentMethodSection: {
			borderTop: '1px solid rgba(0,0,0,0.05)',
		},
	}),
);

export default ShoppingCart;
